import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { Box, Typography } from "@mui/material";
import EasyAnchorLink from "../../UI/AnchorLink";

import { GridColDef } from "@mui/x-data-grid-premium";
import { renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

import "../BrandMetadataReview.css";

interface NonOmniReviewProps {
  chain_id: string;
  country_code: string;
}

const ReviewNonOmniFillRates: React.FC<NonOmniReviewProps> = ({
  chain_id,
  country_code,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>([]);

  const { showError } = useError();
  const { apiRequest } = useApi();
  const columns_def: GridColDef[] = [
    {
      field: "country",
      headerName: "Country",
      description: "The name of the country.",
      type: "string",
      width: 200,
    },
    {
      field: "country_code",
      headerName: "Country Code",
      description: "The country code for the associated data.",
      type: "string",
      width: 150,
    },
    {
      field: "active_count",
      headerName: "Active Count",
      description:
        "The total number of active places from non-omni sources for the brand in the country.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_name_fill_rate",
      headerName: "Name Fill Rate",
      description:
        "The fill rate for the name attribute of places from non-omni sources.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_address_fill_rate",
      headerName: "Address Fill Rate",
      description:
        "The fill rate for the address attribute of places from non-omni sources.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_coordinates_fill_rate",
      headerName: "Coordinates Fill Rate",
      description:
        "The fill rate for the coordinates attribute of places from non-omni sources.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_business_category_fill_rate",
      headerName: "Business Category Fill Rate",
      description:
        "The fill rate for the business category attribute of places from non-omni sources.",
      type: "number",
      width: 200,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_website_fill_rate",
      headerName: "Website Fill Rate",
      description:
        "The fill rate for the website attribute of places from non-omni sources.",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "poi_building_polygon_fill_rate",
      headerName: "Building Polygon Fill Rate",
      description:
        "The fill rate for the building polygon attribute of places from non-omni sources.",
      type: "number",
      width: 200,
      renderCell: renderNumberCell,
    },
  ];

  useEffect(() => {
    if (chain_id && country_code) {
      setLoading(true);
      const params = new URLSearchParams({
        chain_id: chain_id,
        country_code: country_code,
      });

      const fetchFillRates = async () => {
        try {
          const response = await apiRequest<any>(
            `/brand_metadata/non_omni_fillrates_review?${params.toString()}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch places fill rates to review for chain_id ${chain_id} + ${country_code}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchFillRates();
    } else {
      setData([]);
    }
  }, [chain_id, country_code]);

  return (
    <Box className="metadata-review-container">
      <Box display="flex" alignItems="center" id="nonomni-fill-rates">
        <EasyAnchorLink id="nonomni-fill-rates" />
        <Typography variant="h6" gutterBottom sx={{ fontSize: "1.25rem" }}>
          Fill Rates Review
        </Typography>
      </Box>

      <GaneshDataGrid
        gridId="nonOmniReviewFillRates"
        columns={columns_def}
        data={data}
        loading={loading}
        headerFilters={data.length > 0 ? true : false}
        customHeight={data.length > 0 ? "750px" : "300px"}
      />
    </Box>
  );
};

export default ReviewNonOmniFillRates;
